var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { Subject, Observable, of, fromEvent, merge, interval, forkJoin } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { Store, Actions, ofActionDispatched, Select, ofActionCompleted } from '@ngxs/store';
import { map, filter, take, takeUntil, tap, skipUntil, finalize } from 'rxjs/operators';
import { SetupWidget, AuthWidgetFailure, AuthWidget, LogWidgetInteraction } from 'src/app/widget-auth/state/widget.actions';
import { WidgetInteractionEnum } from 'src/app/widget-auth/models/widget-interaction.enum';
import { SelectNode, ToggleVisibleNodes, HideInverse, TogglePickableNodes, ToggleInversePickableNodes, Isolate } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { WidgetAPIHandler } from 'src/app/widget-api/api-handler';
import { ViewState } from 'src/app/view/state/view.state';
import { ViewActions } from 'src/app/view/state/view.actions';
import { WidgetWiring } from 'src/app/shared/widget-wiring';
import { LoadStoryFail, SetIncomingStory, LoadStoryChunkSuccess } from 'src/app/story/state/story.actions';
import { WidgetErrorTypeEnum } from '../../models/widget-error-type.enum';
import { ErrorService } from 'src/app/shared/error.service';
import { EngineService } from 'src/app/engine/engine.service';
import { WidgetState } from 'src/app/widget-auth/state/widget.state';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { SetChapters, SetAllChaptersBackground } from 'src/app/story/state/chapters/story.chapters.actions';
import { CameraService } from 'src/app/engine/camera.service';
import { animationFrame } from 'rxjs/internal/scheduler/animationFrame';
import { SetCamera } from 'src/app/engine/state/camera/engine.camera.actions';
import * as Hammer from 'hammerjs';
import { get, isNil } from 'lodash';
import { LocationStrategy } from '@angular/common';
import { SetScrollMode } from 'src/app/engine/state/engine.actions';
import { BackgroundModel } from 'src/app/engine/models/background.model';
var WidgetMasterComponent = /** @class */ (function () {
    function WidgetMasterComponent(locationStrategy, router, store, actions, activatedRoute, widgetAPIHandler, errorService, engineService, cameraService, renderer) {
        this.locationStrategy = locationStrategy;
        this.router = router;
        this.store = store;
        this.actions = actions;
        this.activatedRoute = activatedRoute;
        this.widgetAPIHandler = widgetAPIHandler;
        this.errorService = errorService;
        this.engineService = engineService;
        this.cameraService = cameraService;
        this.renderer = renderer;
        this.title = 'Biodigital Human';
        this.ngUnsubscribe = new Subject();
    }
    WidgetMasterComponent.prototype.ngOnInit = function () {
        var _this = this;
        // begin error tracking
        this.initErrorHandling();
        // begin interaction tracking
        this.initInteractions();
        var widgetConfig = this.activatedRoute.snapshot.data.widgetConfig;
        this.initWidgetBreakpoints(widgetConfig);
        this.initWidgetSetup(widgetConfig);
        this.initWidgetAuth();
        this.initViewAnalytics();
        this.initInteractionAnalytics();
        // init model default states
        this.initBackgroundFromWidget(widgetConfig);
        this.initBackgroundFromChapter();
        this.initCamera(widgetConfig);
        this.initRotation(widgetConfig);
        // start listening for API requests
        this.widgetAPIHandler.listen();
        // This is a more accurate implementation of 'human.ready'
        this.store
            .select(StoryChaptersState.getAllChaptersModelsReady)
            .pipe(filter(function (event) { return event === true; }), takeUntil(this.ngUnsubscribe))
            .subscribe(function () {
            _this.engineService.fireEvent(EventsEnum.Started, null);
        });
    };
    WidgetMasterComponent.prototype.ngOnDestroy = function () {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    };
    // setup widget config
    WidgetMasterComponent.prototype.initWidgetSetup = function (widgetConfig) {
        // IE11 resets the document referrer on navigation, resulting in an authentication failure.
        // We capture the referrer on page load in a global variable as a workaround. 
        // This code can be removed once we drop support of IE11.
        var embedded = window !== window.top;
        var url = embedded ? BD_DOCUMENT_REFERRER : window.location.href;
        this.store.dispatch(new SetupWidget(url, embedded, widgetConfig));
        // get the current layout
        var layout = this.store.selectSnapshot(ViewState.getLayout);
        // upgrade params to reflect new widget semantics
        var commonConfig = WidgetWiring.convertURIUIConfigToUIConfig(widgetConfig);
        // create a new layout
        var newLayout = WidgetWiring.createLayoutFromConfig(layout, commonConfig);
        // have our new layout take effect
        this.store.dispatch(new ViewActions.SetLayout(newLayout));
        // set default layout after config
        this.store.dispatch(new ViewActions.SetInitialLayout(newLayout));
        // handle widget scroll settings
        var scrollMode = WidgetWiring.getScrollSetting(widgetConfig);
        this.store.dispatch(new SetScrollMode(scrollMode));
        // handle widget loading experience
        var _a = WidgetWiring.getLoader(widgetConfig, this.store.selectSnapshot(ViewState.getLoaderActive), this.store.selectSnapshot(ViewState.getLoaderType)), loaderType = _a.loaderType, isLoaderActive = _a.isLoaderActive;
        // handle widget print setings
        var usePrintImage = WidgetWiring.getUsePrintImage(widgetConfig);
        this.store.dispatch(new ViewActions.SetUsePrintImage(usePrintImage));
        this.store.dispatch([
            new ViewActions.SetLoaderType(loaderType),
            new ViewActions.ToggleLoader(isLoaderActive)
        ]);
    };
    // auth user widget: embedded only
    WidgetMasterComponent.prototype.initWidgetAuth = function () {
        var embedded = this.store.selectSnapshot(WidgetState.getEmbedded);
        if (embedded) {
            this.store.dispatch(new AuthWidget());
        }
    };
    WidgetMasterComponent.prototype.initViewAnalytics = function () {
        var _this = this;
        // init: wait for document ready	
        var loaded = ['loaded', 'interactive', 'complete'].includes(document.readyState);
        var documentReady$ = loaded ? of(loaded) : fromEvent(document, 'DOMContentLoaded');
        documentReady$
            .pipe(take(1), // only initial	
        takeUntil(this.ngUnsubscribe))
            .subscribe(function () {
            _this.store.dispatch(new LogWidgetInteraction(WidgetInteractionEnum.View));
        });
    };
    // analytic: first user interaction	
    WidgetMasterComponent.prototype.initInteractionAnalytics = function () {
        var _this = this;
        // track interaction chance
        this.interactionChance$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(function () {
            _this.store.dispatch(new LogWidgetInteraction(WidgetInteractionEnum.ChanceInteraction));
        });
        // track first interactions 
        this.firstInteraction$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(function () {
            _this.store.dispatch(new LogWidgetInteraction(WidgetInteractionEnum.FirstInteraction));
        });
    };
    // error handling
    WidgetMasterComponent.prototype.initErrorHandling = function () {
        var _this = this;
        // content errors
        this.actions
            .pipe(ofActionDispatched(LoadStoryFail), takeUntil(this.ngUnsubscribe))
            .subscribe(function () { return _this.handleContentErrors(); });
        // engine errors
        this.engineService.getEvent(EventsEnum.Error)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(function (e) { return _this.handleEngineErrors(e); });
        // auth errors
        this.actions.pipe(ofActionDispatched(AuthWidgetFailure), takeUntil(this.ngUnsubscribe))
            .subscribe(function (e) { return _this.handleAuthErrors(e); });
    };
    // sets custom breakpoints
    WidgetMasterComponent.prototype.initWidgetBreakpoints = function (_a) {
        var collapseLabel = _a["ui-collapse-label"], annotationCollapse = _a["annotation-collapse"];
        // get label breakpoint
        var labelBreakpoint = [collapseLabel, annotationCollapse].find(function (value) { return !isNil(value); });
        // validate: 0 allowed
        if (!isNil(labelBreakpoint)) {
            this.store.dispatch(new ViewActions.SetLabelBreakpoint(labelBreakpoint));
        }
    };
    WidgetMasterComponent.prototype.initInteractions = function () {
        // canvas interaction
        var canvasInteractions$ = this.getCanvasInteraction();
        // app interactions
        var appInteractions$ = this.actions.pipe(ofActionDispatched(SelectNode, ToggleVisibleNodes, HideInverse, TogglePickableNodes, ToggleInversePickableNodes, Isolate));
        // observe: interaction ready
        this.interactionReady$ = this.store.select(StoryChaptersState.getSelectedChapter)
            .pipe(filter(function (chapter) { return chapter && chapter.readyState.model; }), take(1));
        // observe: interaction chance, engine scrolled into view
        var interactionElement = this.engineService.getCanvas();
        var intersectionThreshold = 0.75;
        this.interactionChance$ = forkJoin(
        // wait for interaction ready
        this.interactionReady$, 
        // wait for scolled into view
        this.getIntersectionObserver(interactionElement, intersectionThreshold))
            .pipe(take(1));
        // observe: first interaction
        this.firstInteraction$ = merge(canvasInteractions$, appInteractions$)
            .pipe(skipUntil(this.interactionReady$), take(1));
    };
    // set initial background
    WidgetMasterComponent.prototype.initBackgroundFromWidget = function (_a) {
        var _this = this;
        var background = _a.background;
        if (background) {
            this.setWidgetTransparent(BackgroundModel.isTransparent(background));
            // chapters set: patch all backgrounds
            this.actions
                .pipe(ofActionCompleted(SetChapters), take(1), takeUntil(this.ngUnsubscribe))
                .subscribe(function () {
                _this.store.dispatch(new SetAllChaptersBackground(background));
            });
        }
    };
    WidgetMasterComponent.prototype.initBackgroundFromChapter = function () {
        var _this = this;
        this.store.select(StoryChaptersState.getSelectedChapter)
            .pipe(filter(function (chapter) { return chapter != null; }), map(function (chapter) { return BackgroundModel.isTransparent(chapter.background); }), takeUntil(this.ngUnsubscribe))
            .subscribe(function (isTransparent) {
            _this.setWidgetTransparent(isTransparent);
        });
    };
    // set initial camera
    WidgetMasterComponent.prototype.initCamera = function (_a) {
        var _this = this;
        var camera = _a.camera;
        if (camera) {
            // first chunk loaded: set static camera
            this.actions
                .pipe(ofActionDispatched(LoadStoryChunkSuccess), take(1), takeUntil(this.ngUnsubscribe))
                .subscribe(function () {
                _this.store.dispatch(new SetCamera(camera, false));
            });
        }
    };
    // init camera Y rotation
    WidgetMasterComponent.prototype.initRotation = function (_a) {
        var _this = this;
        var degrees = _a["load-rotate"];
        if (degrees) {
            // animate camera rotation
            var timeLast_1 = performance.now();
            interval(0, animationFrame)
                .pipe(map(function () { return degrees * (performance.now() - timeLast_1) * 0.001; }), tap(function () { return timeLast_1 = performance.now(); }), 
            // end animation on interaction
            takeUntil(this.firstInteraction$), takeUntil(this.ngUnsubscribe))
                .subscribe(function (deltaY) {
                _this.cameraService.rotateCameraStatic(0, deltaY);
            });
            // return to original camera
            this.firstInteraction$
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(function () {
                var chapter = _this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
                var chapterCamera = get(chapter, 'camera.location');
                if (chapterCamera) {
                    _this.store.dispatch(new SetCamera(chapterCamera, true, 2));
                }
            });
        }
    };
    WidgetMasterComponent.prototype.handleContentErrors = function () {
        this.store.dispatch(new SetIncomingStory(null, null));
        this.redirectToErrorPage(WidgetErrorTypeEnum.Content);
    };
    WidgetMasterComponent.prototype.handleEngineErrors = function (error) {
        // extract engine errors
        var engineErrorLog = {
            log: error.log,
            stack: error.stack,
            webglInfo: error.webglInfo
        };
        // prep error message
        var errorMessage = {
            category: error.name,
            log: JSON.stringify(engineErrorLog),
            message: error.message
        };
        // log error
        this.errorService.logError(errorMessage)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe();
        // redirect to error
        this.redirectToErrorPage(WidgetErrorTypeEnum.Engine, error.name);
    };
    WidgetMasterComponent.prototype.handleAuthErrors = function (event) {
        this.redirectToErrorPage(WidgetErrorTypeEnum.Auth, event.response.errorCode);
    };
    WidgetMasterComponent.prototype.redirectToErrorPage = function (errorType, errorCode) {
        // don't redirect to /error page if current route is /error
        var url = this.router.url || '';
        if (url.match('/error')) {
            return;
        }
        var basePath = this.locationStrategy.getBaseHref();
        var errorURL = basePath + "/error";
        var urlTree = this.router.createUrlTree([errorURL], {
            queryParams: {
                errorType: errorType,
                errorCode: errorCode,
                errorUrl: this.router.url
            }
        });
        var redirectPath = this.router.serializeUrl(urlTree);
        window.location.href = redirectPath;
    };
    WidgetMasterComponent.prototype.getCanvasInteraction = function () {
        var _this = this;
        var inputManager;
        return new Observable(function (observer) {
            // manage canvas interactions
            var canvas = _this.engineService.getCanvas();
            inputManager = new Hammer.Manager(canvas);
            inputManager.add([
                new Hammer.Tap({ event: 'singletap' }),
                new Hammer.Tap({ event: 'doubletap', taps: 2 }),
                // handle mouse down/touch down
                new Hammer.Pan({ event: 'pan', direction: Hammer.DIRECTION_ALL, threshold: 0 })
            ]);
            // !important: capture 'this' context
            var handleCanvasInteraction = function () { return observer.next(); };
            var handleHammerEvent = handleCanvasInteraction.bind(_this);
            // trigger user interaction
            inputManager.on('panstart', handleHammerEvent);
            inputManager.on('singletap', handleHammerEvent);
        }).pipe(
        // cleanup hammer event handling
        finalize(function () {
            if (inputManager) {
                inputManager.destroy();
                inputManager = null;
            }
        }), takeUntil(this.ngUnsubscribe));
    };
    WidgetMasterComponent.prototype.getIntersectionObserver = function (targetEl, intersectionThreshold) {
        return new Observable(function (observer) {
            // intersection config
            var intersectionCconfig = {
                root: null,
                threshold: intersectionThreshold
            };
            // create intersection observer
            var intersectionObserver = new IntersectionObserver(function (entries) {
                // emit observed + cleanup
                var isIntersecting = entries[0].intersectionRatio >= intersectionThreshold;
                if (isIntersecting) {
                    // emit observer + cleanup
                    observer.next(targetEl);
                    observer.complete();
                    intersectionObserver.unobserve(targetEl);
                }
            }, intersectionCconfig);
            // begin intersection observing
            intersectionObserver.observe(targetEl);
        });
    };
    WidgetMasterComponent.prototype.setWidgetTransparent = function (isTransparent) {
        (isTransparent) ?
            this.renderer.setStyle(document.body, 'background', 'none transparent') :
            this.renderer.removeStyle(document.body, 'background');
    };
    __decorate([
        Select(WidgetState.getEmbedded),
        __metadata("design:type", Observable)
    ], WidgetMasterComponent.prototype, "embedded$", void 0);
    __decorate([
        Select(WidgetState.getParentURL),
        __metadata("design:type", Observable)
    ], WidgetMasterComponent.prototype, "parentURL$", void 0);
    return WidgetMasterComponent;
}());
export { WidgetMasterComponent };
